import { b2x } from '@b2x/react/src';
import React from 'react';

import { AccountOffcanvasProps, useAccountOffcanvas } from './AccountOffcanvas';
import { CartOffcanvasProps, useCartOffcanvas } from './CartOffcanvas';

interface AppContextInterface {
  AccountOffcanvas: React.FunctionComponentElement<AccountOffcanvasProps>;
  CartOffcanvas: React.FunctionComponentElement<CartOffcanvasProps>;
  headerCheckout: boolean;
  headerHeight?: number;
  isFooterCopyrightVisible: boolean;
  isTopBarRowVisible: boolean;
  thingsToLoadBeforeAppReady: Record<string, unknown>;
  topBarRowRef: React.RefObject<HTMLDivElement>;
  transparentHeader: boolean;
}

export const [AppContextProvider, useAppContext] = b2x.createContext<AppContextInterface>('AppContext');

interface AppStaticContextInterface {
  setFooterCopyrightRef: React.RefCallback<HTMLDivElement>;
  setHeaderCheckout: React.Dispatch<React.SetStateAction<boolean>>;
  setHeaderHeight: React.Dispatch<React.SetStateAction<number | undefined>>;
  setThingsToLoadBeforeAppReady: React.Dispatch<React.SetStateAction<Record<string, unknown>>>;
  setTopBarDesktopHeight: React.Dispatch<React.SetStateAction<number | undefined>>;
  setTopBarRowRef: React.RefCallback<HTMLDivElement>;
  setTransparentHeader: React.Dispatch<React.SetStateAction<boolean>>;
  showAccountOffcanvas(): void;
  showCartOffcanvas(): void;
}

export const [AppStaticContextProvider, useAppStaticContext] =
  b2x.createContext<AppStaticContextInterface>('AppStaticContext');

interface UseAppContextInitializerProps {}

const useAppContextInitializer = (props: UseAppContextInitializerProps) => {
  const [transparentHeader, setTransparentHeader] = React.useState<boolean>(false);
  const [AccountOffcanvas, showAccountOffcanvas] = useAccountOffcanvas();
  const [CartOffcanvas, showCartOffcanvas] = useCartOffcanvas();
  const [headerCheckout, setHeaderCheckout] = React.useState<boolean>(false);
  const [headerHeight, setHeaderHeight] = React.useState<number>();
  const [footerCopyrightRef, setFooterCopyrightRef, isFooterCopyrightVisible] =
    b2x.useIntersectionObserver<HTMLDivElement>(true);

  const [thingsToLoadBeforeAppReady, setThingsToLoadBeforeAppReady] = React.useState<Record<string, unknown>>({});

  const [topBarDesktopHeight, setTopBarDesktopHeight] = React.useState<number>();
  const [topBarRowRef, setTopBarRowRef, isTopBarRowVisible] = b2x.useIntersectionObserver<HTMLDivElement>(true);

  const appContext: AppContextInterface = React.useMemo(
    () => ({
      AccountOffcanvas,
      CartOffcanvas,
      footerCopyrightRef,
      headerCheckout,
      headerHeight: headerHeight,
      isFooterCopyrightVisible,
      isTopBarRowVisible,
      thingsToLoadBeforeAppReady,
      topBarDesktopHeight,
      topBarRowRef,
      transparentHeader,
    }),
    [
      AccountOffcanvas,
      topBarRowRef,
      CartOffcanvas,
      headerCheckout,
      footerCopyrightRef,
      thingsToLoadBeforeAppReady,
      headerHeight,
      topBarDesktopHeight,
      isFooterCopyrightVisible,
      isTopBarRowVisible,
      transparentHeader,
    ]
  );

  const appStaticContext: AppStaticContextInterface = React.useMemo(
    () => ({
      setFooterCopyrightRef,
      setHeaderCheckout,
      setHeaderHeight,
      setThingsToLoadBeforeAppReady,
      setTopBarDesktopHeight,
      setTopBarRowRef,
      setTransparentHeader,
      showAccountOffcanvas,
      showCartOffcanvas,
    }),
    [showAccountOffcanvas, showCartOffcanvas, setTopBarRowRef, setFooterCopyrightRef]
  );

  return {
    AppContextProvider,
    AppStaticContextProvider,
    appContext,
    appStaticContext,
  };
};

export interface AppContextProps extends UseAppContextInitializerProps {
  children:
    | React.ReactNode
    | ((appContext: AppContextInterface, appStaticContext: AppStaticContextInterface) => React.ReactNode);
}

export const AppContext = ({ children, ...otherProps }: AppContextProps) => {
  const appContextInitializer = useAppContextInitializer(otherProps);
  return (
    <appContextInitializer.AppContextProvider value={appContextInitializer.appContext}>
      <appContextInitializer.AppStaticContextProvider value={appContextInitializer.appStaticContext}>
        {typeof children === 'function'
          ? children(appContextInitializer.appContext, appContextInitializer.appStaticContext)
          : children}
      </appContextInitializer.AppStaticContextProvider>
    </appContextInitializer.AppContextProvider>
  );
};
